import { api, withFreshAccess } from './client'
import { db, upsertPost, logFailure } from '../cache/db'
import { gunzipJson } from '../cache/gzip'
import { nextAccount } from './accountPool'
import { allImagesOf } from '../lib/embedMedia'
import { warmImageCache } from './warmImages'
import type { PostEmbedView } from '../components/PostMedia'

const BATCH = 25
// 30 days. Hydrated AppView posts shouldn't expire mid-session — the
// gallery / likers / lightbox all read these. Eviction is a user
// action (Settings → wipe local data) not a rolling TTL.
const TTL_MS = 30 * 24 * 60 * 60 * 1000

interface HydratedPost {
  uri: string
  cid: string
  author: {
    did: string
    handle: string
    displayName?: string
    avatar?: string
    labels?: { val: string }[]
  }
  record: {
    text?: string
    reply?: { parent?: { uri?: string }; root?: { uri?: string } }
    /** Self-labels (`!hide`/`porn`/`sexual`/etc) the author attached at
     *  post-create time. */
    labels?: { values?: { val: string }[] }
  }
  /** AppView aggregate labels (mod + author + post). */
  labels?: { val: string }[]
  embed?: Record<string, unknown>
  indexedAt: string
  likeCount?: number
  repostCount?: number
  replyCount?: number
}

/**
 * Hydrate a list of post URIs by batching com.atproto.repo.getPosts (25
 * URIs per call) through the active account's session and writing each
 * resulting AppView post into db.posts. Skips entries already cached
 * within TTL_MS so repeated views are cheap.
 */
export async function hydratePostUris(uris: string[]): Promise<Map<string, HydratedPost>> {
  const out = new Map<string, HydratedPost>()
  if (uris.length === 0) return out

  const acc = nextAccount()
  if (!acc) return out

  // One batch read for everything we already have in db.posts.
  const now = Date.now()
  const rows = await db.posts.bulkGet(uris)
  const need: string[] = []
  // Decoded cached posts for the URIs we're about to (re)fetch. Kept so a
  // post AppView no longer returns — deleted upstream, author blocked /
  // suspended, or unliked-then-deleted — can still render from cache. Its
  // image bytes are pinned in Cache Storage by the service worker, so the
  // tile keeps loading exactly as it did when the post was live. This is
  // what makes "preserve deleted images" work for the likes / reposts
  // gallery sources, not just the target's own deleted-posts view.
  const staleByUri = new Map<string, HydratedPost>()
  for (let i = 0; i < uris.length; i++) {
    const c = rows[i]
    let cachedVal: HydratedPost | null = null
    if (c) {
      try {
        const val: HydratedPost = c.z
          ? await gunzipJson<HydratedPost>(c.z)
          : c.value as unknown as HydratedPost
        if (val && typeof val.uri === 'string') cachedVal = val
      } catch {
        /* unreadable row — treat as a cache miss */
      }
    }
    if (c && cachedVal && now - c.fetched_at < TTL_MS) {
      // If a cached post has a non-empty embed but allImagesOf extracts
      // nothing, the entry was likely cached before a new embed type
      // (e.g. gallery#view) was introduced. Refetch for the correct
      // structure — but keep the stale copy so we don't LOSE the tile if
      // AppView has since dropped the post entirely.
      const embedObj = cachedVal.embed as Record<string, unknown> | undefined
      const hasEmbed = !!embedObj && Object.keys(embedObj).length > 0
      const noImages = hasEmbed && allImagesOf(cachedVal.embed as PostEmbedView, cachedVal.author?.did).length === 0
      if (noImages) {
        need.push(uris[i])
        staleByUri.set(uris[i], cachedVal)
      } else {
        out.set(uris[i], cachedVal)
      }
    } else {
      // TTL-expired or truly uncached — refetch. Stash any decoded stale
      // copy (TTL-expired rows still hold a perfectly good post) so a
      // now-deleted post still renders from cache instead of vanishing.
      need.push(uris[i])
      if (cachedVal) staleByUri.set(uris[i], cachedVal)
    }
  }
  if (need.length === 0) {
    warmImageCache([...out.values()])
    return out
  }

  // Batch the rest.
  for (let i = 0; i < need.length; i += BATCH) {
    const slice = need.slice(i, i + BATCH)
    const qs = slice.map((u) => 'uris=' + encodeURIComponent(u)).join('&')
    try {
      const resp = await withFreshAccess(acc.id, () =>
        api.xrpc<{ posts?: HydratedPost[] }>({
          accountId: acc.id,
          method: 'GET',
          path: `app.bsky.feed.getPosts?${qs}`,
        }),
      )
      for (const p of resp.posts ?? []) {
        out.set(p.uri, p)
        const at = parseAt(p.uri)
        if (at) {
          await upsertPost({
            uri: p.uri,
            cid: p.cid,
            did: at.did,
            rkey: at.rkey,
            value: p as unknown as Record<string, unknown>,
            fetched_at: now,
          })
        }
      }
    } catch {
      // ignore, partial hydration is fine
    }
  }

  // Fall back to cache for anything AppView didn't return. A post that's
  // been deleted / blocked / suspended keeps its cached copy (and pinned
  // image bytes) so the gallery still shows it. Only URIs with no cache at
  // all are genuinely unrenderable — those get logged so the operator can
  // see why a handful of tiles never appear.
  const unrecoverable: string[] = []
  for (const u of need) {
    if (out.has(u)) continue
    const stale = staleByUri.get(u)
    if (stale) out.set(u, stale)
    else unrecoverable.push(u)
  }
  if (unrecoverable.length > 0) {
    void logFailure({
      op: 'gallery',
      target: 'hydratePostUris (subject unavailable)',
      reason:
        'post unavailable upstream (deleted / blocked / suspended) with no cached copy — tile cannot render',
      occurrences: unrecoverable.length,
      sample: unrecoverable[0],
    })
  }

  warmImageCache([...out.values()])
  return out
}

function parseAt(uri: string): { did: string; rkey: string } | null {
  const m = uri.match(/^at:\/\/([^/]+)\/[^/]+\/([^/]+)$/)
  if (!m) return null
  return { did: m[1], rkey: m[2] }
}

export type { HydratedPost }
